import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Tabs,
  Tab,
  TextField
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@mui/material";
import { useUser } from '../../services/userContext';
import { useNavigate } from 'react-router-dom';
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GridToolbar } from "@mui/x-data-grid";
import { toast } from 'react-toastify';
import { useCallback } from 'react';
import LargeBondDetailsModal from './LargeBondDetailsModal';
const LargeBondLandingPage = () => {
  const { user } = useUser();
  const [hasLargeBondAccess, setHasLargeBondAccess] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [bonds, setBonds] = useState([]);
  const [filteredBonds, setFilteredBonds] = useState([]);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const navigate = useNavigate();
  const [selectedBondDetails, setSelectedBondDetails] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [bondToCancel, setBondToCancel] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");



  const OPEN_STATUSES = ['AP', 'PD', 'FP', 'FP1', 'FP2'];
  const CLOSED_STATUSES = ['NW', 'WWA', 'UAP', 'FC'];

  useEffect(() => {
    const fetchPermissions = async () => {
      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;

      if (!token) return;

      try {
        const userDetailsRes = await fetch(`${baseURL}user/info`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!userDetailsRes.ok) throw new Error('Failed to fetch user info');

        const userDetails = await userDetailsRes.json();
        const { subAgentId, agencyId, dbaAgencyName, firstName, lastName } = userDetails;

        

        const permissionsRes = await fetch(`${baseURL}User/${agencyId}/permissions/${subAgentId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!permissionsRes.ok) throw new Error('Failed to fetch permissions');

        const permissions = await permissionsRes.json();
        const hasPermission = permissions.some(
          (p) => p.portalPermissionTypeID === 11 && p.permit === true
        );

        setHasLargeBondAccess(hasPermission);
      } catch (error) {
        console.error('Error checking Large Bond permission:', error);
        setHasLargeBondAccess(false);
      }
    };

    fetchPermissions();
  }, []);

  const fetchBonds = useCallback(async () => {
    const token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;
  
    try {
      const res = await fetch(`${baseURL}largebond/bonds`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      const data = await res.json();
      setBonds(data);
    } catch (err) {
      console.error('Failed to fetch large bonds:', err);
    }
  }, []);

  useEffect(() => {
    if (!hasLargeBondAccess) return;
  
    fetchBonds(); // fetch on page load
  
    const intervalId = setInterval(() => {
      fetchBonds(); // refresh every 15s
    }, 15000);
  
    return () => clearInterval(intervalId);
  }, [hasLargeBondAccess, fetchBonds]);



  useEffect(() => {
    const filter = () => {
      const filtered = bonds.filter(bond => {
        const statusList = tabIndex === 0 ? OPEN_STATUSES : CLOSED_STATUSES;
        let matchesStatus = statusList.includes(bond.status);

        if (tabIndex === 0) {
          if (statusFilter === "APPROVED") {
            matchesStatus = bond.status === "AP";
          } else if (statusFilter === "PENDING") {
            matchesStatus = bond.status === "PD";
          }
        }

        if (tabIndex === 1) {
          const createdDate = new Date(bond.dateCreated);
          const sixMonthsAgo = new Date();
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

          // show only bonds from last 6 months to avoid bad data
          if (createdDate < sixMonthsAgo) return false;

          // filter from date range
          if (dateRange.from || dateRange.to) {
            const fromDate = dateRange.from ? new Date(dateRange.from) : null;
            const toDate = dateRange.to ? new Date(dateRange.to) : null;

            if (fromDate && createdDate < fromDate) return false;
            if (toDate && createdDate > toDate) return false;
          }
        }

        return matchesStatus;
      });

      setFilteredBonds(filtered);
    };

    filter();
  }, [tabIndex, bonds, dateRange, statusFilter]);

  const handleEdit = (row) => {
    const bondToEdit = encodeURIComponent(JSON.stringify(row));
    window.location.href = `/edit-large-bond?data=${bondToEdit}`;
  };

  if (hasLargeBondAccess === null) return null;
  if (!hasLargeBondAccess) {
    return (
      <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1">
          You do not have permission to access the Large Bond section.
        </Typography>
      </Box>
    );
  }

  const handleCancel = (row) => {
    setBondToCancel(row);
    setCancelConfirmOpen(true);
  };
  
  const confirmCancel = async () => {
    if (!bondToCancel) return;
  
    const token = localStorage.getItem("jwtToken");
    const baseURL = process.env.REACT_APP_BASE_URL;
  
    try {
      const res = await fetch(`${baseURL}largebond/${bondToCancel.largeBondId}/cancel`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
  
      if (!res.ok) throw new Error("Failed to cancel bond");
  
      toast.success(`✅ Bond ${bondToCancel.refNumber} cancelled.`);
      // Refresh bond list
      setBonds((prev) =>
        prev.map((b) =>
          b.largeBondId === bondToCancel.largeBondId
            ? { ...b, status: "NW" }
            : b
        )
      );
    } catch (err) {
      toast.error("❌ Failed to cancel bond.");
      console.error(err);
    } finally {
      setCancelConfirmOpen(false);
      setBondToCancel(null);
    }
  };


  const handleView = async (row) => {
    const token = localStorage.getItem("jwtToken");
    const baseURL = process.env.REACT_APP_BASE_URL;

    try {
      const res = await fetch(`${baseURL}largebond/${row.largeBondId}/details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Failed to fetch bond details");

      const data = await res.json();
      setSelectedBondDetails(data);
      setViewOpen(true);
    } catch (err) {
      toast.error("❌ Failed to load bond details.");
      console.error(err);
    }
  };

  

  return (
    <Container maxWidth="lg" sx={{ paddingTop: 4 }}>
      <Box>
        

        <Paper sx={{
            padding: 3,
            mb: 3,
            borderRadius: 1,
            backgroundColor: '#f9f9f9',
            boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
          }}>
            <Typography variant="h6" gutterBottom>
              Manage Large Bond Submissions
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This section allows you to create large bond requests. Key features include:
            </Typography>
            <ul style={{ paddingLeft: '1.25rem', marginBottom: 0 }}>
              <li>Submit a new large bond request</li>
              <li>Clone previous bonds for faster entry</li>
            </ul>

            {/* 🔽 Button moved here */}
            <Box sx={{ mt: 3 }}>
              <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={() => navigate('/largebond/create')}>
                Request Large Bond
              </Button>
            </Box>
        </Paper>

        <Tabs value={tabIndex} onChange={(_, newValue) => setTabIndex(newValue)}>
          <Tab label="Open Bonds" />
          <Tab label="Closed Bonds" />
        </Tabs>
        {tabIndex === 0 && (
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Filter by Bond Status:
              </FormLabel>
              <RadioGroup
                row
                name="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <FormControlLabel
                  value="ALL"
                  control={<Radio color="primary" />}
                  label="Show All"
                />
                <FormControlLabel
                  value="APPROVED"
                  control={<Radio color="primary" />}
                  label="Approved Only"
                />
                <FormControlLabel
                  value="PENDING"
                  control={<Radio color="primary" />}
                  label="Pending Only"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {tabIndex === 1 && (
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              label="From Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            />
            <TextField
              label="To Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
            />
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <DataGrid
            rows={filteredBonds}
            columns={[
              { field: 'refNumber', headerName: 'Ref #', flex: 1 },
              { field: 'defendantName', headerName: 'Defendant', flex: 1 },
              {
                field: 'agencyName',
                headerName: 'Agency',
                flex: 1,
                renderCell: (params) => (
                  <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: 1.2 }}>
                    {params.value}
                  </div>
                )
              },
              {
                field: 'status',
                headerName: 'Status',
                flex: 1,
                renderCell: (params) => {
                  const codeToLabel = {
                    AP: 'Approved',
                    PD: 'Pending',
                    FP: 'File Pending',
                    FP1: 'File Pending - 1st Letter',
                    FP2: 'File Pending - 2nd Letter',
                    FC: 'File Complete',
                    WWA: 'Written Without Approval'
                  };
                  return codeToLabel[params.value] || params.value;
                }
              },
              {
                field: 'amount',
                headerName: 'Amount',
                flex: 1,
                renderCell: (params) =>
                  params.value?.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }) || '$0.00'
              },
              {
                field: 'dateCreated',
                headerName: 'Date Created',
                flex: 1,
                renderCell: (params) => {
                  const date = new Date(params.value);
                  if (isNaN(date)) return 'Invalid Date';
                  return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  });
                }
              },
              ...(tabIndex === 0
                ? [
                    {
                      field: 'actions',
                      headerName: 'Actions',
                      flex: 1,
                      renderCell: (params) => (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {['PD', 'AP'].includes(params.row.status) && (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEdit(params.row)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                          )}
                          <Tooltip title="Cancel Bond">
                            <IconButton size="small" onClick={() => handleCancel(params.row)}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Clone">
                            <IconButton
                              size="small"
                              onClick={() =>
                                window.location.href = `/largebond/create?id=${params.row.largeBondId}&copy=true`
                              }
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => handleView(params.row)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )
                    },
                  ]
                : []),
            ]}
            getRowId={(row) => row.largeBondId}
            autoHeight
            disableSelectionOnClick
            pageSize={5}
            rowsPerPageOptions={[5]}
            components={{ Toolbar: GridToolbar }}
            initialState={{
              sorting: {
                sortModel: [
                  { field: "dateCreated", sort: "desc" } // ⬅️ Newest first
                ]
              }
            }}
          />
        </Box>
      </Box>
      <LargeBondDetailsModal
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        bond={selectedBondDetails}
      />
      <Dialog open={cancelConfirmOpen} onClose={() => setCancelConfirmOpen(false)}>
        <DialogTitle>Confirm Cancellation</DialogTitle>
        <DialogContent>
          Are you sure you want to cancel bond {bondToCancel?.refNumber}?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)}>No</Button>
          <Button onClick={confirmCancel} color="error" variant="contained">
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LargeBondLandingPage;
